import { FC, useState } from 'react';
import { IonButton, IonModal, IonHeader, IonToolbar, IonTitle, IonContent, IonToggle, IonItem, IonLabel, IonSelect, IonSelectOption, IonCheckbox } from '@ionic/react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { Card } from '../components/Card';
import { CloseIcon } from '../theme/Icons';
import { DevSettingsWriter } from '../support/DevSettings';
import { useDevSettings } from '../utils/useDevSettings';
import { RecordingService } from './RecordingService';
import { IRecordingStatus } from './RecordingState';

interface ITestRecordingToolsModalProps {
    isOpen: boolean;
    onDismiss: () => void;
}

export const TestRecordingToolsModal: FC<ITestRecordingToolsModalProps> = ({
    isOpen,
    onDismiss
}) => {
    const devSettings = useDevSettings();
    const devSettingsWriter = useInstance(DevSettingsWriter);
    const recordingService = useInstance(RecordingService);

    const [selectedStatus, setSelectedStatus] = useState<IRecordingStatus>('upload-failed');
    const [shouldShowErrorModal, setShouldShowErrorModal] = useState(true);

    const ERROR_STATUSES: IRecordingStatus[] = ['upload-failed', 'transcribe-failed', 'summarize-failed'];

    const isErrorStatus = (status: IRecordingStatus): boolean => {
        return ERROR_STATUSES.includes(status);
    };

    const handleStatusChange = (status: IRecordingStatus) => {
        setSelectedStatus(status);
        // Auto-enable error modal checkbox if selecting an error status
        if (isErrorStatus(status)) {
            setShouldShowErrorModal(true);
        } else {
            setShouldShowErrorModal(false);
        }
    };

    const handleToggleProcessing = async (checked: boolean) => {
        await devSettingsWriter.setSetting('disableRecordingProcessing', checked);
    };

    const handleInsertTestRecording = async () => {
        try {
            await recordingService.addTestRecording({
                status: selectedStatus,
                msDuration: 120000,
                transcription: 'Test transcription for development',
                shouldShowErrorModal: shouldShowErrorModal && isErrorStatus(selectedStatus)
            });
            onDismiss();
        } catch (error) {
            console.error('Failed to insert test recording:', error);
        }
    };

    return (
        <IonModal isOpen={isOpen} onDidDismiss={onDismiss} data-id="test-recording-tools-modal">
            <IonHeader>
                <IonToolbar>
                    <IonTitle>Test Recording Tools</IonTitle>
                    <IonButton slot="end" fill="clear" onClick={onDismiss}>
                        <CloseIcon />
                    </IonButton>
                </IonToolbar>
            </IonHeader>

            <IonContent className="ion-padding">
                <Card>
                    <div style={{padding: '16px'}}>
                        <IonItem lines="none" style={{marginBottom: '16px'}}>
                            <IonLabel>DisableRecordingProcessing</IonLabel>
                            <IonToggle
                                checked={devSettings.disableRecordingProcessing}
                                onIonChange={(e) => handleToggleProcessing(e.detail.checked)}
                            />
                        </IonItem>

                        <IonItem lines="none" style={{marginBottom: '16px'}}>
                            <IonLabel>Status:</IonLabel>
                            <IonSelect
                                value={selectedStatus}
                                onIonChange={(e) => handleStatusChange(e.detail.value)}
                                interface="popover"
                            >
                                <IonSelectOption value="upload-failed">Upload Failed</IonSelectOption>
                                <IonSelectOption value="recording-complete">Recording Complete</IonSelectOption>
                                <IonSelectOption value="offline-pending">Offline Pending</IonSelectOption>
                                <IonSelectOption value="uploading">Uploading</IonSelectOption>
                                <IonSelectOption value="uploaded">Uploaded</IonSelectOption>
                                <IonSelectOption value="transcribe-failed">Transcribe Failed</IonSelectOption>
                                <IonSelectOption value="summarize-failed">Summarize Failed</IonSelectOption>
                            </IonSelect>
                        </IonItem>

                        <IonItem lines="none" style={{marginBottom: '16px', opacity: isErrorStatus(selectedStatus) ? 1 : 0.5}}>
                            <IonCheckbox
                                checked={shouldShowErrorModal}
                                disabled={!isErrorStatus(selectedStatus)}
                                onIonChange={(e) => setShouldShowErrorModal(e.detail.checked)}
                            />
                            <IonLabel style={{marginLeft: '8px'}}>
                                Display Error Modal
                            </IonLabel>
                        </IonItem>

                        <IonButton
                            expand="block"
                            onClick={handleInsertTestRecording}
                            data-id="insert-test-recording-button"
                        >
                            Insert Test Recording
                        </IonButton>
                    </div>
                </Card>
            </IonContent>
        </IonModal>
    );
};
